/**
 * Main-thread client for the evaluation worker (docs/SHEETS.md 4.1).
 *
 * Two responsibilities, both of which exist because the worker cannot be trusted to
 * police itself:
 *
 * 1. **Timeouts.** A worker stuck inside a SymPy call runs no timer of its own.
 *    Terminating it from here is the only reliable stop, which is also why
 *    evaluation must never run on the main thread -- there would be nothing left to
 *    do the terminating.
 *
 * 2. **Supersession.** An engineer holding the `+` stepper generates passes faster
 *    than SymPy answers. Every reply carries the id of the request that caused it,
 *    and anything but the newest is dropped, so results can never arrive out of
 *    order and paint a stale number over a fresh one.
 */
import type { CellOutcome, EvalRequestCell, WorkerRequest, WorkerResponse } from './protocol'
import { isWorkerResponse } from './protocol'
import { LIMITS } from './limits'
import type { ResultValue } from '../model/types'

/** The part of `Worker` this client uses. Narrow, so a test can supply a fake. */
export interface WorkerLike {
  postMessage(message: unknown): void
  terminate(): void
  addEventListener(type: 'message', handler: (event: { data: unknown }) => void): void
  addEventListener(type: 'error', handler: (event: unknown) => void): void
}

export type EngineStatus = 'idle' | 'booting' | 'ready' | 'failed'

export interface EvaluatePass {
  readonly outcomes: readonly CellOutcome[]
  readonly scope: Readonly<Record<string, ResultValue>>
  /** True when a newer pass superseded this one before it finished. */
  readonly superseded: boolean
}

const EMPTY_PASS: EvaluatePass = { outcomes: [], scope: {}, superseded: true }

export class SheetEngine {
  private worker: WorkerLike | null = null
  private status: EngineStatus = 'idle'
  private engineVersion = ''
  private nextId = 1
  private newestId = 0
  private bootWaiters: { resolve: () => void; reject: (err: Error) => void }[] = []
  private pending = new Map<
    number,
    { resolve: (pass: EvaluatePass) => void; timer: ReturnType<typeof setTimeout> }
  >()

  constructor(private readonly createWorker: () => WorkerLike) {}

  getStatus(): EngineStatus {
    return this.status
  }

  getEngineVersion(): string {
    return this.engineVersion
  }

  async boot(): Promise<void> {
    if (this.status === 'ready') return
    if (this.status === 'booting') {
      return new Promise<void>((resolve, reject) => {
        this.bootWaiters.push({ resolve, reject })
      })
    }

    this.status = 'booting'
    const worker = this.createWorker()
    this.worker = worker

    worker.addEventListener('message', (event: { data: unknown }) => {
      this.onMessage(event.data)
    })
    worker.addEventListener('error', () => {
      this.failBoot('sheets.error.worker_unavailable')
    })

    const booted = new Promise<void>((resolve, reject) => {
      this.bootWaiters.push({ resolve, reject })
    })

    const timer = setTimeout(() => {
      this.failBoot('sheets.error.worker_boot_timeout')
    }, LIMITS.workerBootMs)

    worker.postMessage({ type: 'boot' } satisfies WorkerRequest)

    try {
      await booted
    } finally {
      clearTimeout(timer)
    }
  }

  /**
   * Runs one pass. `cells` must already be in document order and limited to what
   * actually needs recomputing -- deciding that is the graph's job, not the
   * engine's.
   */
  async evaluate(
    cells: readonly EvalRequestCell[],
    carried: Readonly<Record<string, ResultValue>>,
  ): Promise<EvaluatePass> {
    if (cells.length === 0) return { outcomes: [], scope: carried, superseded: false }
    if (this.status !== 'ready' || !this.worker) {
      throw new Error('sheets.error.worker_unavailable')
    }

    const id = this.nextId
    this.nextId += 1
    this.newestId = id

    return new Promise<EvaluatePass>((resolve) => {
      const timer = setTimeout(() => {
        // The worker is wedged. Terminating is the only way back, and everything in
        // flight is reported as timed out rather than left spinning.
        this.pending.delete(id)
        this.hardReset()
        resolve({
          outcomes: cells.map((cell) => ({
            cellId: cell.cellId,
            ok: false,
            error: { kind: 'timeout', messageKey: 'sheets.error.sheet_timeout' },
          })),
          scope: carried,
          superseded: false,
        })
      }, LIMITS.sheetHardMs)

      this.pending.set(id, { resolve, timer })
      this.worker?.postMessage({
        type: 'evaluate',
        id,
        cells,
        carried,
      } satisfies WorkerRequest)
    })
  }

  dispose(): void {
    for (const [, entry] of this.pending) {
      clearTimeout(entry.timer)
      entry.resolve(EMPTY_PASS)
    }
    this.pending.clear()
    this.worker?.terminate()
    this.worker = null
    this.status = 'idle'
  }

  private hardReset(): void {
    this.worker?.terminate()
    this.worker = null
    this.status = 'failed'
    for (const [, entry] of this.pending) {
      clearTimeout(entry.timer)
      entry.resolve(EMPTY_PASS)
    }
    this.pending.clear()
  }

  private failBoot(messageKey: string): void {
    if (this.status === 'ready') return
    this.status = 'failed'
    this.worker?.terminate()
    this.worker = null
    const waiters = this.bootWaiters
    this.bootWaiters = []
    for (const waiter of waiters) waiter.reject(new Error(messageKey))
  }

  private onMessage(data: unknown): void {
    if (!isWorkerResponse(data)) return
    const message: WorkerResponse = data

    switch (message.type) {
      case 'booted': {
        this.status = 'ready'
        this.engineVersion = message.engineVersion
        const waiters = this.bootWaiters
        this.bootWaiters = []
        for (const waiter of waiters) waiter.resolve()
        return
      }
      case 'boot-failed':
        this.failBoot(message.messageKey)
        return
      case 'result': {
        const entry = this.pending.get(message.id)
        if (!entry) return
        clearTimeout(entry.timer)
        this.pending.delete(message.id)
        entry.resolve({
          outcomes: message.outcomes,
          scope: message.scope,
          superseded: message.id !== this.newestId,
        })
        return
      }
      case 'failed': {
        const entry = this.pending.get(message.id)
        if (!entry) return
        clearTimeout(entry.timer)
        this.pending.delete(message.id)
        entry.resolve({
          outcomes: [],
          scope: {},
          superseded: message.id !== this.newestId,
        })
        return
      }
    }
  }
}

/**
 * The real worker. Pyodide is served from our own origin, never a CDN -- a CDN in
 * the evaluation path is a supply-chain hole in the one place we least want one
 * (CLAUDE.md 21.1, SHEETS.md 4.1). `yarn pyodide` vendors it into `public/pyodide/`.
 */
export function createSheetWorker(): WorkerLike {
  return new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' })
}
